import React from 'react';
import {
  Box,
  Center,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import FormatNumber from '../../../components/FormatNumber';
import { PaginatorFooter } from '../../../components/PaginatorFooter';
import { Formatter } from '../../../utils/Formatter';
import { PaginatedPaymentLinks } from '../models/payment-link-view';
import { PaymentLinkViewStatusBadge } from './PaymentLinkViewStatusBadge';
import { CopyLinkButton } from './CopyLinkButton';
import { ShareLinkButton } from './ShareLinkButton';
import { PaymentLinksVisibleColumns, defaultVisibleColumns } from './ColumnsMenu';

interface PaymentLinksTableProps {
  data: PaginatedPaymentLinks | null;
  loading: boolean;
  error: string | null;
  visibleColumns?: PaymentLinksVisibleColumns;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

/**
 * Tabla de solo lectura para el listado paginado de links de pago. Presentacional:
 * recibe el resultado ya mapeado a `PaginatedPaymentLinks` (con `records` ya filtrados
 * por `PaymentLinksFilters`/el padre) y delega la obtención de datos al hook que la use
 * (actualmente `useBroxelPaymentLinks`). Qué columnas se muestran lo controla
 * `visibleColumns` (ver `ColumnsMenu`); los filtros viven fuera de la tabla, en
 * `PaymentLinksFilters` (mismo comportamiento que `TransactionFilters`).
 */
export const PaymentLinksTable = (props: PaymentLinksTableProps) => {
  const { data, loading, error, visibleColumns = defaultVisibleColumns(), onNextPage, onPreviousPage } = props;
  const records = data?.records ?? [];
  const isEmpty = !loading && !error && records.length === 0;
  const columnCount = visibleColumns.length || 1;
  const isVisible = (key: PaymentLinksVisibleColumns[number]) => visibleColumns.includes(key);

  return (
    <Box backgroundColor="white" borderRadius="16px" padding="xxl">
      <TableContainer overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              {isVisible('concept') && <Th>Concepto</Th>}
              {isVisible('amount') && <Th>Monto</Th>}
              {isVisible('status') && <Th>Estado</Th>}
              {isVisible('createdAt') && <Th>Fecha de creación</Th>}
              {isVisible('expireDate') && <Th>Fecha de expiración</Th>}
              {isVisible('link') && <Th>Link</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {loading &&
              <Tr>
                <Td colSpan={columnCount} paddingY="xxl">
                  <Center color="neutral.600"><Spinner /></Center>
                </Td>
              </Tr>
            }

            {!loading && !!error &&
              <Tr>
                <Td colSpan={columnCount} paddingY="xxl">
                  <Center color="destructive.700">{error}</Center>
                </Td>
              </Tr>
            }

            {isEmpty &&
              <Tr>
                <Td colSpan={columnCount} paddingY="xxl">
                  <Center><Text color="neutral.600">No hay registros disponibles</Text></Center>
                </Td>
              </Tr>
            }

            {!loading && !error && records.map((link) => (
              <Tr key={link.id}>
                {isVisible('concept') && <Td fontSize="s">{link.concept}</Td>}
                {isVisible('amount') &&
                  <Td fontSize="s">
                    <FormatNumber value={link.amount} style="currency" currency={link.currencyCode || 'MXN'} locale="es-MX" />
                  </Td>
                }
                {isVisible('status') && <Td fontSize="s"><PaymentLinkViewStatusBadge status={link.status} /></Td>}
                {isVisible('createdAt') && <Td fontSize="s">{Formatter.toDate(link.createdAt).dateString}</Td>}
                {isVisible('expireDate') &&
                  <Td fontSize="s">{link.expireDate ? Formatter.toDate(link.expireDate).dateString : '—'}</Td>
                }
                {isVisible('link') &&
                  <Td fontSize="s">
                    <Flex gap="s" alignItems="center">
                      {link.url && <CopyLinkButton url={link.url} />}
                      <ShareLinkButton id={link.id} />
                    </Flex>
                  </Td>
                }
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {!loading && !error && data &&
        <Box marginTop="xl">
          <PaginatorFooter
            pageable={{ page_number: data.currentPage, page_size: data.pageSize }}
            total_elements={data.totalRecords}
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage} />
        </Box>
      }
    </Box>
  );
};
